import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Switch } from 'antd-mobile';
import { useSelector } from 'react-redux';
import { useLocalStorageState, useSetState } from 'ahooks';
import { Skeleton } from 'antd';
import { FrameAnimation } from 'common-package';
import { Icon } from '@fuxi/eevee-icon';
import { debounce } from 'lodash-es';
import { ScrollView, SlideSelect, Stepper, When } from '@/components';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { genMinMaxSteps } from '@/components/stepper';
import musicDecoImg from '@/assets/image/music-deco.png';
import { SocketClientType } from '@/service/socketio';
import { selectCurrentServingOrder } from '@/store/service';
import { ALL_PAGES, IS_OPEN_IMMER_MODE, storagePrefixV2 } from '@/constant';
import { useNav } from '@/hooks';
import { isNewSoftwareVersion } from '@/utils';
import { MassageControlValueContext } from '../index';

import cx from './index.module.scss';

interface Props {
  className?: string;
  isSocketConnected: boolean;
  socketInstance: React.MutableRefObject<SocketClientType>;
  controlItems: GetMassageControlPannelResponse[0]['controlItemConfigs'];
  isShow: boolean;
  bgmState: GetMachineStateResponse['background_music'];
}
type OpenState = 'on' | 'off';

export interface FuncAdjusterRefAttrs {
  setBroadcastState: React.Dispatch<React.SetStateAction<OpenState>>;
  setMicOpenState: React.Dispatch<React.SetStateAction<OpenState>>;
  setBgmState: React.Dispatch<React.SetStateAction<Props['bgmState']>>;
}

const SOUND_WAVE_BG = storagePrefixV2 + '/sound-wave-2-min.png';

const SOUND_WAVE_BG_V2 = storagePrefixV2 + '/animation/sound-wave-v2.png';

const FuncAdjuster = forwardRef<FuncAdjusterRefAttrs, Props>(
  ({ className, isSocketConnected, socketInstance, controlItems, isShow, bgmState: bgmStateFromInterface }, ref) => {
    const currentServingOrder = useSelector(selectCurrentServingOrder);
    const [broadcastState, setBroadcastState] = useState<OpenState>('off');
    const [micOpenState, setMicOpenState] = useState<OpenState>('off');
    const [isIniting, setIsIniting] = useState(true);
    const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
    const [bgmOpenState, setBgmState] = useSetState<Props['bgmState']>({
      enable: false,
      playing: false,
      name: null,
    });
    const [isPlayingBgm, setIsPlayingBgm] = useState(false);
    const [isOpenImmerMode, setIsOpenImmerMode] = useLocalStorageState(IS_OPEN_IMMER_MODE, {
      defaultValue: true,
    });
    const { allValues, setAllValues, currentMassageType } = useContext(MassageControlValueContext);
    const nav = useNav();

    const initState = useCallback(async () => {
      try {
        const micState = await socketInstance.current.getMicrophoneState();
        const broadState = await socketInstance.current.getBroadcastState();
        setMicOpenState(micState?.enable ? 'on' : 'off');
        setBroadcastState(broadState?.enable ? 'on' : 'off');
      } finally {
        setTimeout(() => setIsIniting(false), 400);
      }
    }, [socketInstance]);

    // 设置麦克风和播报模式
    const handleSetVoiceState = useCallback(
      async (type: 'mic' | 'bgm' | 'broadcast', enable: boolean) => {
        switch (type) {
          case 'mic':
            await socketInstance.current.setMicrophone({ enable });
            setMicOpenState(enable ? 'on' : 'off');
            break;
          case 'broadcast':
            await socketInstance.current.setBroadcast({ enable });
            setBroadcastState(enable ? 'on' : 'off');
            break;
          case 'bgm':
            await socketInstance.current.setBackgroundMusic({ enable });
            break;
        }
      },
      [socketInstance]
    );

    const handleInvokeRobotService = useMemo(
      () =>
        debounce((activeConfigItem: Massage.ControlItemOptions, value) => {
          console.log('InvokeRobotService', value);

          switch (activeConfigItem) {
            case 'robot.voice':
              socketInstance.current.setVolume({ volume: value });
              break;
          }
        }, 300),
      [socketInstance]
    );

    const handleOnChange = useCallback(
      async (activeConfigItem: Massage.ControlItemOptions, value) => {
        setAllValues(prev => ({ ...prev, [activeConfigItem]: value }));
        handleInvokeRobotService(activeConfigItem, value);
      },
      [handleInvokeRobotService, setAllValues]
    );

    useImperativeHandle(
      ref,
      () => ({
        setBroadcastState,
        setMicOpenState,
        setBgmState,
      }),
      [setBgmState]
    );

    useEffect(() => {
      if (bgmStateFromInterface) {
        setBgmState(bgmStateFromInterface);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bgmStateFromInterface]);

    useEffect(() => {
      if (bgmOpenState?.playing) {
        setIsPlayingBgm(true);
      }
    }, [bgmOpenState?.playing]);

    useEffect(() => {
      if (isSocketConnected) {
        initState();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSocketConnected]);

    useEffect(() => {
      if (!isIniting) {
        setTimeout(() => scrollViewRef.current?.handleRefresh(), 50);
      }
    }, [isIniting, isShow, bgmOpenState?.enable]);

    return (
      <ScrollView ref={scrollViewRef} className={cx('func-adjuster', className)}>
        <div>
          <Skeleton
            loading={isIniting}
            paragraph={{
              rows: 8,
              width: '100%',
            }}
            title={false}
            avatar={false}
            active={true}
          >
            {controlItems?.find(item => item.name === 'robot.microphone') && (
              <div className={cx('control-item')}>
                <div className={cx('label')}>麦克风开启</div>
                {isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.10.0') ? (
                  <SlideSelect
                    value={micOpenState}
                    activeColor="white"
                    activeBold={true}
                    labelWidth={120}
                    steps={[
                      {
                        value: 'on',
                        label: '开启',
                      },
                      {
                        value: 'off',
                        label: '关闭',
                      },
                    ]}
                    onChange={value => {
                      handleSetVoiceState('mic', value === 'on');
                    }}
                  />
                ) : (
                  <Switch
                    onChange={checked => handleSetVoiceState('mic', checked)}
                    checked={micOpenState === 'on'}
                    style={{
                      '--width': '48px',
                      '--height': '28px',
                      '--border-width': '2px',
                    }}
                  />
                )}
              </div>
            )}
            {controlItems?.find(item => item.name === 'robot.bgm') &&
            // v1.10.0版本新增了沉浸模式实时按摩头功能
            isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.10.0') ? (
              <div className={cx('control-item', 'immer-v2-bgm-item')}>
                <div className={cx('wrapper')}>
                  <div className={cx('label')}>
                    <div className={cx('music-label')}>
                      <span>沉浸模式</span>
                      <img className={cx('music-deco-img')} src={musicDecoImg} alt="" />
                    </div>
                  </div>
                  <SlideSelect
                    value={isOpenImmerMode ? 'on' : 'off'}
                    activeColor="white"
                    activeBold={true}
                    labelWidth={120}
                    steps={[
                      {
                        value: 'on',
                        label: '开启',
                      },
                      {
                        value: 'off',
                        label: '关闭',
                      },
                    ]}
                    onChange={value => {
                      setIsOpenImmerMode(value === 'on');
                      handleSetVoiceState('bgm', value === 'on');
                    }}
                  />
                </div>
                <div className={cx('entry-immer-mode')}>
                  <div className={cx('entry-mode-box', { show: isOpenImmerMode })}>
                    <div className={cx('mode-item', 'immer')} onClick={() => nav(ALL_PAGES.IMMERSION_MODE)}>
                      <div>「疗愈场景」</div>
                      <div>点击进入</div>
                    </div>
                    <When condition={currentMassageType === 'Massage'}>
                      <div
                        className={cx('mode-item', 'real-time')}
                        onClick={() =>
                          nav(ALL_PAGES.IMMERSION_MODE, {
                            state: { isRealTimeMode: true },
                          })
                        }
                      >
                        <div>「实时场景」</div>
                        <div>点击进入</div>
                      </div>
                    </When>
                  </div>
                </div>
              </div>
            ) : (
              // 背景音乐功能在1.8.5及以上版本才有
              isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.8.5') && (
                <div className={cx('control-item', 'bgm-item')}>
                  <div
                    className={cx('label')}
                    onClick={() =>
                      isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.9.0') &&
                      nav(ALL_PAGES.IMMERSION_MODE)
                    }
                  >
                    <div className={cx('music-label')}>
                      <span>疗愈音乐</span>
                      <When condition={isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.9.0')}>
                        <img className={cx('music-deco-img')} src={musicDecoImg} alt="" />
                      </When>
                    </div>
                  </div>
                  <div
                    className={cx('animation')}
                    onClick={() =>
                      isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.9.0') &&
                      nav(ALL_PAGES.IMMERSION_MODE)
                    }
                  >
                    {isPlayingBgm && isShow ? (
                      <FrameAnimation
                        count={260}
                        aspectRatio={5.18}
                        width={325}
                        duration={5}
                        picDirection="column"
                        url={
                          isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.9.0')
                            ? SOUND_WAVE_BG_V2
                            : SOUND_WAVE_BG
                        }
                        onSinglePlayComplete={() => {
                          // 单帧播放完成后，如果机器人未在播音乐，则隐藏动画
                          if (!bgmOpenState?.playing) {
                            setIsPlayingBgm(false);
                          }
                        }}
                      />
                    ) : (
                      !!isNewSoftwareVersion(currentServingOrder?.device?.softwareVersion, '1.9.0') && (
                        <div className={cx('immer-tip-text')}>点击进入沉浸模式</div>
                      )
                    )}
                  </div>
                  <Switch
                    onChange={checked => handleSetVoiceState('bgm', checked)}
                    checked={bgmOpenState?.enable}
                    style={{
                      '--width': '48px',
                      '--height': '28px',
                      '--border-width': '2px',
                    }}
                  />
                </div>
              )
            )}
            {controlItems?.find(item => item.name === 'robot.broadcast') && (
              <div className={cx('control-item')}>
                <div className={cx('label')}>
                  <div>讲解模式</div>
                  <div className={cx('voice-tips')}>
                    <Icon className={cx('icon')} name="info-circle" />
                    <span>
                      {broadcastState === 'on'
                        ? '详细模式含功效讲解和情绪引导播报'
                        : '简洁模式下仅保留少量理疗状态提示'}
                    </span>
                  </div>
                </div>
                <SlideSelect
                  value={broadcastState}
                  activeColor="white"
                  activeBold={true}
                  labelWidth={120}
                  steps={[
                    {
                      value: 'off',
                      label: '简洁',
                    },
                    {
                      value: 'on',
                      label: '详细',
                    },
                  ]}
                  onChange={value => handleSetVoiceState('broadcast', value === 'on')}
                />
              </div>
            )}
            {controlItems?.find(item => item.name === 'robot.voice') && (
              <div className={cx('control-item', 'voice-item')}>
                <div className={cx('label')}>音量</div>
                <div className={cx('setter')}>
                  <Stepper
                    {...genMinMaxSteps(controlItems?.find(item => item.name === 'robot.voice')?.values)}
                    value={allValues['robot.voice'] as number}
                    ignoreValueDisabled={true}
                    onChange={value => handleOnChange('robot.voice', value)}
                  />
                </div>
              </div>
            )}
          </Skeleton>
        </div>
      </ScrollView>
    );
  }
);

export default FuncAdjuster;
